/**
 * SDLC project service.
 *
 * The SDLC workspace is a set of dependent stages (Idea → Requirements →
 * Design → Documentation → Code → Tests → CI/CD → Deployment). Each stage
 * produces an artifact that the next stage consumes. We store the entire
 * "current project" in `sessionStorage` so the user can navigate freely
 * between tabs without losing context.
 *
 * The service is intentionally framework-agnostic — components subscribe
 * to changes via the `subscribe()` callback which fires on every mutation
 * (including those from other tabs of the SDLC workspace).
 *
 * Schema (all keys optional):
 *   {
 *     idea: { title, summary, answers: {...} } | null,
 *     requirements: { reportId, snapshot } | null,
 *     architecture: { options: [...], selectedId, approval, comment } | null,
 *     docs: { files: [...], approval, comment } | null,
 *     code: { codeId, snapshot } | null,
 *     tests: { framework, files: [...] } | null,
 *     cicd: { provider, files: [...] } | null,
 *     deployment: { provider, files: [...], approval, comment } | null,
 *     pipeline: { jobId, status, result } | null,
 *     updatedAt: ISO string
 *   }
 */

const STORAGE_KEY = 'sdlcProject_v1';
const listeners = new Set();

const _read = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (e) {
    console.warn('[sdlcProject] failed to parse stored project', e);
    return {};
  }
};

const _write = (project) => {
  const next = { ...project, updatedAt: new Date().toISOString() };
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch (e) {
    console.warn('[sdlcProject] failed to persist project', e);
  }
  listeners.forEach((fn) => {
    try { fn(next); } catch (err) { console.warn('[sdlcProject] listener error', err); }
  });
  return next;
};

export const getProject = () => _read();

export const setStage = (stage, data) => {
  const cur = _read();
  return _write({ ...cur, [stage]: data });
};

export const patchStage = (stage, patch) => {
  const cur = _read();
  const prev = cur[stage] || {};
  return _write({ ...cur, [stage]: { ...prev, ...patch } });
};

export const setStageApproval = (stage, approved, comment = '') => {
  return patchStage(stage, {
    approval: {
      approved: Boolean(approved),
      comment,
      approvedAt: approved ? new Date().toISOString() : null,
    },
  });
};

export const setStageComment = (stage, comment) => {
  const cur = _read();
  const prevApproval = cur?.[stage]?.approval || {};
  return patchStage(stage, {
    approval: {
      ...prevApproval,
      comment,
    },
  });
};

export const clearProject = () => {
  sessionStorage.removeItem(STORAGE_KEY);
  listeners.forEach((fn) => { try { fn({}); } catch (e) { /* noop */ } });
};

export const subscribe = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

/** Convenience: returns the full requirements report object (or null). */
export const getRequirementsReport = () => {
  const p = _read();
  return p?.requirements?.snapshot || null;
};

/** Convenience: returns the selected architecture option (or null). */
export const getSelectedArchitecture = () => {
  const arch = _read()?.architecture;
  if (!arch || !Array.isArray(arch.options) || !arch.selectedId) return null;
  return arch.options.find((o) => o.id === arch.selectedId) || null;
};

/** Convenience: returns the generated code snapshot (or null). */
export const getCodeSnapshot = () => {
  return _read()?.code?.snapshot || null;
};

export const getPipelinePayload = () => {
  const project = _read();
  const requirements = project?.requirements?.snapshot || null;
  const architecture = getSelectedArchitecture();
  return {
    idea: project?.idea || null,
    requirements_report: requirements,
    architecture,
    docs: project?.docs || null,
    code: project?.code?.snapshot || null,
    tests: project?.tests || null,
    cicd: project?.cicd || null,
    deployment: project?.deployment || null,
    approvals: Object.fromEntries(
      Object.entries(project || {})
        .filter(([, value]) => value?.approval)
        .map(([key, value]) => [key, value.approval])
    ),
  };
};
